/**
 * ATS Edit User Dialog Component
 */

import { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';

const ATSEditUserDialog = ({ api, open, onOpenChange, user, onSuccess }) => {
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                tek_id: user.tek_id || '',
                location: user.location || '',
                shift: user.shift || '',
                ats_role: user.ats_role || '',
                experience: user.experience || '',
                department: user.department || ''
            });
        }
    }, [user]);

    const handleSave = async () => {
        if (!user) return;
        
        setSaving(true);
        try {
            await api.put(`/ats/users/${user.id}`, formData);
            toast.success('User updated successfully');
            onSuccess?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update user');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Edit ATS User</DialogTitle>
                    <DialogDescription>
                        Update user details. Email cannot be changed.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="edit-name">Name</Label>
                        <Input
                            id="edit-name"
                            value={formData.name || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            data-testid="ats-edit-name-input"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-tek-id">Tek ID</Label>
                            <Input
                                id="edit-tek-id"
                                value={formData.tek_id || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, tek_id: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-department">Department</Label>
                            <Input
                                id="edit-department"
                                value={formData.department || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-location">Location</Label>
                            <Input
                                id="edit-location"
                                value={formData.location || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-shift">Shift</Label>
                            <Input
                                id="edit-shift"
                                value={formData.shift || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, shift: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-ats-role">ATS Role</Label>
                            <Input
                                id="edit-ats-role"
                                value={formData.ats_role || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, ats_role: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-experience">Experience</Label>
                            <Input
                                id="edit-experience"
                                value={formData.experience || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
                            />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="ats-edit-save-btn">
                        {saving ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                        )}
                        Save Changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ATSEditUserDialog;
